import axios from "axios";
import { useEffect, useRef, useState } from "react";
// @mui
import { AddShoppingCart, Check, Close } from "@mui/icons-material";
import { alpha, Box, Button, Card, CircularProgress, Dialog, DialogActions, DialogContent, FormControl, Grid, InputLabel, MenuItem, Select, Stack, ToggleButton, Tooltip, Typography, useTheme } from "@mui/material";
// components
import { IconButton } from "src/components/IconButton";
// hooks
import useResponsive from "src/hooks/useResponsive";
import useAppContext from "src/hooks/useAppContext";
//
import { WEB_API } from "../../config";
import { toCurrency } from "src/utils/number";
import { enqueueSnackbar, useSnackbar } from "notistack";

function ItemDialog({ onClose, data, setAdded }) {
  const [flavors, setFlavors] = useState({});
  const [secondFlavor, setSecondFlavor] = useState(false);

  const ingredients = data.flavors?.reduce((p, c, idx) => ({ ...p, [c]: data.ingredients?.[idx] || [] }), {}) || {};

  const { addToCart } = useAppContext();
  const { enqueueSnackbar } = useSnackbar();
  const isDesktop = useResponsive('up', 'md');

  return (
    <Dialog
      open
      fullWidth
      maxWidth='sm'
      onClose={onClose}
    >
      <IconButton
        size="small"
        color="default"
        onClick={onClose}
        sx={{ position: 'absolute', top: 16, right: 16 }}
      >
        <Close />
      </IconButton>

      <DialogContent sx={{ overflow: 'auto', display: 'flex', flexDirection: 'column', alignItems: 'center', rowGap: 2 }}>
        <Typography variant="h5">
          {data.name}
        </Typography>

        {Boolean(data.flavors?.length) &&
          <Grid container spacing={1} sx={{ mb: 1 }}>
            <FormControl fullWidth>
              <InputLabel>{secondFlavor ? 'Selecione o primeiro sabor' : 'Selecione o sabor'}</InputLabel>
              <Select
                fullWidth
                value={flavors[0] ?? ''}
                onChange={(e) => setFlavors(prev => ({ ...prev, 0: e.target.value }))}
                native={!isDesktop}
                label={secondFlavor ? 'Selecione o primeiro sabor' : 'Selecione o sabor'}
              >
                {(!isDesktop) && (
                  <option value="" disabled />
                )}
                {data.flavors.map(i =>
                  isDesktop ? (
                    <MenuItem key={i} value={i}>{i}</MenuItem>
                  )
                    : <option key={i} value={i}>{i}</option>
                )}
              </Select>

              <Typography variant="body2" sx={{ mt: 0.5, ml: 1 }}>
                {ingredients[flavors[0]]?.join(', ')}
              </Typography>
            </FormControl>

            {data.nFlavors && Number(data.nFlavors) > 1 &&
              <Stack direction={'row'} justifyContent={'center'} sx={{ mt: 2, width: '100%' }}>
                {secondFlavor ?
                  <FormControl fullWidth>
                    <InputLabel>Selecione o segundo sabor</InputLabel>
                    <Select
                      fullWidth
                      value={flavors[1] ?? ''}
                      onChange={(e) => setFlavors(prev => ({ ...prev, 1: e.target.value }))}
                      native={!isDesktop}
                      label={'Selecione o segundo sabor'}
                    >
                      {(!isDesktop) && (
                        <option value="" disabled />
                      )}
                      {data.flavors.map(i =>
                        isDesktop ? (
                          <MenuItem key={i} value={i}>{i}</MenuItem>
                        )
                          : <option key={i} value={i}>{i}</option>
                      )}
                    </Select>

                    <Typography variant="body2" sx={{ mt: 0.5, ml: 1 }}>
                      {ingredients[flavors[1]]?.join(', ')}
                    </Typography>
                  </FormControl>
                  :
                  <Button
                    variant="contained"
                    onClick={() => setSecondFlavor(true)}
                  >
                    Quero dois sabores
                  </Button>
                }
              </Stack>
            }
          </Grid>
        }
      </DialogContent>

      <DialogActions>
        <Button
          variant="contained"
          onClick={() => {
            if (!Object.values(flavors)?.length) {
              enqueueSnackbar("Escolha pelo menos um sabor!", { variant: 'warning' });
              return;
            }
            addToCart(
              {
                ...data,
                availableFlavors: data.flavors,
                flavors: Object.values(flavors).sort(),
                availableIngredients: data.ingredients,
                ingredients: Object.values(flavors)?.sort().map(f => ingredients[f] || []),
                localId: Date.now()
              }
            );
            setAdded(true);
            onClose();
          }}
        >
          Salvar
        </Button>
      </DialogActions>
    </Dialog >
  )
}

function Item({ data }) {
  const [added, setAdded] = useState(false);
  const [dialog, setDialog] = useState(null);
  const [openTooltip, setOpenTooltip] = useState(false);

  const theme = useTheme();
  const { addToCart, cart, deliveryStatus } = useAppContext();

  useEffect(() => {
    if (cart.find(e => e.id === data.id))
      setAdded(true);
    // eslint-disable-next-line
  }, [cart])

  return (
    <Card
      sx={{
        p: 2,
        mx: 0.5,
        height: '100%',
        borderRadius: 2,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        bgcolor: alpha(theme.palette.background.default, 0.1)
      }}
    >
      <Typography variant="h6">
        {data.name}
      </Typography>

      {Number(data.nFlavors) !== 2 &&
        <Typography variant="caption">
          {data.ingredients?.join(', ')}
        </Typography>
      }

      <Stack direction={'row'} justifyContent={'space-between'} alignItems={'center'}>
        <Box>
          {data.originalPrice &&
            <Typography variant="body2" fontWeight={'bold'} sx={{ textDecoration: 'line-through' }}>
              {toCurrency(data.originalPrice)}
            </Typography>
          }

          <Typography variant="body2" fontWeight={'bold'} {...data.originalPrice && { color: theme.palette.success.light, fontSize: 16 }}>
            {toCurrency(data.price)}
          </Typography>
        </Box>

        {/* {!added && */}
        <Tooltip
          open={openTooltip}
          title={deliveryStatus?.open ? 'Adicionar ao pedido' : 'Não é possível realizar pedidos neste momento. Verifique os horários de funcionamento'}
        >
          <span
            onMouseEnter={() => setOpenTooltip(true)}
            onMouseLeave={() => setOpenTooltip(false)}
          >
            <IconButton
              disabled={!deliveryStatus?.open || !Number(data.q)}
              onClick={() => {
                if (data.flavors?.length) {
                  setDialog(data);
                }
                else {
                  addToCart({ ...data, localId: Date.now() });
                  enqueueSnackbar("Item adicionado ao carrinho", { variant: 'success' });
                  setAdded(true);
                }
              }}
              sx={{ bgcolor: theme.palette.primary.main, position: 'relative', alignSelf: 'end' }}
            >
              <AddShoppingCart />
            </IconButton>
          </span>
        </Tooltip>
        {/* } */}
      </Stack>

      {!Number(data.q) &&
        <Typography sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', mt: 1 }} color={theme.palette.error.main}>
          Item esgotado!
        </Typography>
      }

      {added &&
        <Typography sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', mt: 1 }} color={theme.palette.success.light}>
          <Check sx={{ mr: 0.5 }} />
          Adicionado ao carrinho
        </Typography>
      }

      {dialog &&
        <ItemDialog
          data={dialog}
          setAdded={setAdded}
          onClose={() => setDialog(null)}
        />
      }
    </Card>
  )
}

export default function Menu() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState('');
  const [categories, setCategories] = useState([]);
  const [filteredData, setFilteredData] = useState(null);

  const theme = useTheme();
  const isDesktop = useResponsive('up', 'md');

  useEffect(() => {
    axios.request({
      url: `${WEB_API}/list_categories`,
      method: 'GET',
      headers: {
        'Access-Control-Allow-Origin': '*',
        Accept: '*/*'
      }
    }).then(resp => {
      setCategories(resp.data);
      setLoading(false);
    }).catch(err => {
      console.error(err);
      setLoading(false);
    })

    axios.request({
      url: `${WEB_API}/list_items`,
      method: 'GET',
      headers: {
        'Access-Control-Allow-Origin': '*',
        Accept: '*/*'
      }
    }).then(res => {
      setData(res.data);
      setLoading(false);
    }).catch(err => {
      console.error(err);
      setLoading(false);
    })
  }, [])

  const itemsRef = useRef(null);

  const scrollToItems = () => {
    itemsRef.current.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    if (filteredData && !isDesktop) {
      scrollToItems();
    }
  }, [filteredData, isDesktop, itemsRef]);

  const filter = (category) => setFilteredData(data?.filter((d) => d.category === category));

  return (
    <Card sx={{ p: 2, m: 2, mb: 10, borderRadius: 3 }}>
      <Typography variant="h4" sx={{ mb: 2 }}>
        Cardápio
      </Typography>

      {loading &&
        <CircularProgress />
      }

      <Stack direction='row' justifyContent={'center'} flexWrap={'wrap'} spacing={{ sm: 1 }} rowGap={1}>
        {categories.map((c) => (
          <ToggleButton
            key={c.name}
            value={c.name}
            selected={selected === c.name}
            onChange={() => {
              setSelected(c.name);
              if (selected !== c.name) {
                filter(c.name);
              }
            }}
            sx={{
              color: 'white',
              borderRadius: 3,
              bgcolor: theme.palette.primary.main,
              width: { xs: '100%', sm: '24%' },
              '&:hover': {
                bgcolor: `${theme.palette.primary.dark} !important`
              },
              '&.Mui-selected': {
                bgcolor: theme.palette.error.dark,
                '&:hover': {
                  bgcolor: `${theme.palette.error.dark} !important`
                }
              },
            }}
          >
            <img src={`${c.name.toLowerCase()}.png`} width={80} height={80} alt='' />
            <Typography variant={isDesktop ? 'h6' : 'body1'} fontWeight={500} sx={{ mx: 1 }}>
              {c.name}
            </Typography>
          </ToggleButton>
        ))}
      </Stack>

      <Grid container rowGap={1} sx={{ mt: 2 }} ref={itemsRef}>
        {filteredData?.map((d) => (
          <Grid item xs={12} sm={12} md={3} key={d.id}>
            <Item data={d} />
          </Grid>
        ))}
      </Grid>
    </Card>
  )
}